import { NgClass } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { APPOINTMENT_CONSTANT } from '../../../../constants/appointment-constants';
import { TRANSLATION_TEMPLATES } from '../../../../constants/translation-templates-constants';
import {
  PARENT_TO_CHILD_IFRAME_EVENTS, PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS, PARENT_TO_CHILD_IFRAME_VALIDATION_CONTROL_NAME, WIDGET_CONSTANTS
} from '../../../../constants/widget-constants';
import { CustomBookingMessageTemplate } from '../../../../db-models/widget-conf-dao';
import { CustomerProfile, Partner } from '../../../../models/global';
import { AppointmentState } from '../../../../models/state.model';
import { WidgetColorConf } from '../../../../models/widget-color.model';
import { CustomEventService } from '../../../../services/custom-event.service';
import { HelperService } from '../../../../services/helper.service';
import { LoggerService } from '../../../../services/logger.service';
import { WidgetUtilService } from '../../../../services/widget-util.service';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { TranslationPipe } from '../../../../shared/pipes/translation.pipe';
import { CwCardComponent } from '../../../common/theme/cw-card/cw-card.component';

@Component({
  selector: 'app-appointment-customer-profiles',
  templateUrl: './appointment-customer-profiles.component.html',
  styleUrls: ['./appointment-customer-profiles.component.scss'],
  imports: [NgClass, CwCardComponent, FontAwesomeModule, ButtonComponent, TranslateModule, TranslationPipe]
})
export class AppointmentCustomerProfilesComponent implements OnInit, OnChanges {

  @Input() widgetColorConf: WidgetColorConf;
  @Input() appointmentState: AppointmentState;
  @Input() partner: Partner;
  @Input() lang: string;
  @Input() nextButtonTemplate: CustomBookingMessageTemplate;
  @Input() customerProfileUuid: string;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];

  @Output() navigateToEvent = new EventEmitter<any>();

  private helperService = inject(HelperService);

  readonly appointmentConstant = APPOINTMENT_CONSTANT;
  readonly parentToChildIframeEventActions = PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS;
  readonly parentToChildIframeEvents = PARENT_TO_CHILD_IFRAME_EVENTS;
  readonly parentToChildIframeValidationControlName = PARENT_TO_CHILD_IFRAME_VALIDATION_CONTROL_NAME;
  readonly templateContent = TRANSLATION_TEMPLATES;

  protected stepErrorTemplate: CustomBookingMessageTemplate;
  protected readonly showStepValidationError = signal(false);

  constructor(
    private widgetUtilService: WidgetUtilService,
    private customEventService: CustomEventService
  ) { }

  ngOnInit(): void {
    this.setupStepErrorTemplate();

    // Auto-select customer profile if UUID is provided via deeplink
    if (this.customerProfileUuid && this.partner?.customer_profiles?.length > 0) {
      const matchedProfile = this.partner.customer_profiles.find(
        profile => profile.uuid === this.customerProfileUuid
      );
      if (matchedProfile) {
        LoggerService.log('[CustomerProfile] Auto-selecting profile via deeplink:', matchedProfile.uuid);
        this.onSelectCustomerProfile(matchedProfile);
        // Defer navigation to next tick to allow profile selection to complete
        setTimeout(() => {
          this.navigateTo(this.appointmentConstant.APPOINTMENT);
        }, 0);
      } else {
        LoggerService.warn('[CustomerProfile] No matching profile found for UUID:', this.customerProfileUuid);
      }
    }

    this.customEventService.iFrameNavigationFromParent.subscribe({
      next: (action: string) => {
        if (
          action === this.parentToChildIframeEventActions.NEXT &&
          this.appointmentState.customerProfileId
        ) {
          this.navigateToEvent.next({ page: this.appointmentConstant.APPOINTMENT });
        } else if (!this.appointmentState.customerProfileId) {
          this.widgetUtilService.sendMessageToParent({
            eventName: this.parentToChildIframeEvents.VALIDATION,
            controls: [
              this.parentToChildIframeValidationControlName.CUSTOMER_PROFILE
            ]
          });
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.widgetTemplates?.currentValue) {
      this.widgetTemplates?.length && this.setupStepErrorTemplate();
    }
  }

  private setupStepErrorTemplate(): void {
    this.stepErrorTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_STEP_ERROR_CUSTOMER_TYPE
    );
  }

  onSelectCustomerProfile(customerProfile: CustomerProfile) {
    this.appointmentState.customerProfileId = customerProfile.id;
    this.widgetUtilService.customerProfile = customerProfile;
  }

  navigateTo(page: string) {
    this.navigateToEvent.next({ page: page });
  }

  protected onNextButtonClick(): void {
    if (Number(this.partner?.widget_buttons_always_enabled) === 1 && !this.appointmentState.customerProfileId) {
      this.showStepValidationError.set(true);
      return;
    }
    this.showStepValidationError.set(false);
    this.navigateTo(this.appointmentConstant.APPOINTMENT);
  }
}
